// Report-wide number/copy formatting, shared by the section subtitles and the section bodies so a
// figure reads the same in both places.

import { escapeHTML } from '@aytm/lib/common/func'

// Rates here span two magnitudes, so one precision cannot serve both: completion sits in the tens of
// percent, where a decimal is noise, while a dedup or blocked-attempt rate is routinely a fraction of
// a percent, which whole-percent rounding flattens to a "0%" that contradicts the non-zero count
// beside it. Hence three bands — whole percent from one percent up, one decimal below it, and below
// the smallest rate a decimal can carry an upper bound, so a non-zero figure is never captioned a
// flat zero. The bound is quoted at a tenth — the finest figure the decimal band prints — rather
// than at the cut-off it stands for, which would state a precision the report never otherwise shows.
export const pctBanded = n => {
  if (n == null) return null
  const pct = n * 100
  if (1 <= pct) return String(Math.round(pct))
  if (0.05 <= pct) return String(Math.round(pct * 10) / 10)
  return 0 < pct ? '<0.1' : '0'
}

// An unavailable rate is an em dash; a real zero is a figure and still reads "0%".
export const pctOrDash = n => (n == null ? '—' : `${pctBanded(n)}%`)

// Drop-offs are the complement of completions. Only the completion rate is on the wire, so the
// survey's own share and the platform average are both stated as the drop-off side of it.
export const dropOffPct = completionRate => pctOrDash(completionRate == null ? null : 1 - completionRate)

// Author-written question wording carries editor formatting. Its tags are removed rather than
// escaped into view: stripping deletes the img/a an escape would only display, and whatever the
// strip cannot match is still escaped, so nothing surviving it can parse as an element. A line
// break becomes a space, or the words either side of it run together. The strip requires a letter
// after the angle bracket, exactly as a parser does, so a comparison in the wording ("5 < 10 and
// 20 > 15") is not read as a tag and deleted.
export const plainQuestion = text =>
  escapeHTML(
    String(text ?? '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/?[a-z][^>]*>/gi, '')
  )

// The stale-data notice names the load its figures came from. Date and time both matter: a report
// refreshed through the day is stale by minutes, one left open overnight by a day.
export const loadedAtLabel = ts => {
  const at = new Date(ts)
  const time = at.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
  return `${at.toLocaleDateString('en-US')} ${time}`
}
